import PixelStatus from '@/Enums/Pixels/PixelStatus.enum';
import { type IControlOption } from '@/Interfaces/ControlOption.interface';
import type { IParsedEvent } from '@/Interfaces/Pixels/Event.interface';
import ArchiveButton, { type IArchiveButtonProps } from '@/components/Grid/ArchiveButton';
import ControlOptionChip from '@/components/Grid/ControlOptionChip';
import LoadingCellWrapper from '@/components/Grid/LoadingCellWrapper';
import ViewButton from '@/components/Grid/ViewButton';
import StatusChip from '@/components/StatusChip';
import { getActiveInactiveStatusProps } from '@/components/StatusChip/StatusProps';
import { defaultQuickFilter } from '@/helpers/grid/quickFilter';
import { fallbackValueFormatter } from '@/helpers/grid/valueFormatter.helpers';
import { pixelsStatusOperators } from '@/helpers/pixels/grid';
import { dateToShortFormat } from '@/helpers/valueFormatter/dateFormatter.helpers';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { Link } from '@mui/material';
import { type GridColDef, type GridRenderCellParams, getGridStringQuickFilterFn } from '@mui/x-data-grid-pro';
import PixelFiredByDayCell from './PixelFiredByDayCell';

export const getColumns = (
  onViewPixelClick: (row: IParsedEvent) => void,
  archivePixel: IArchiveButtonProps['onClick'],
  loadingMetrics: boolean,
  isAdmin: boolean,
): GridColDef[] =>
  [
    {
      field: 'name',
      headerName: 'Name',
      getApplyQuickFilterFn: getGridStringQuickFilterFn,
      display: 'flex',
      renderCell: ({ value, row }: GridRenderCellParams) => (
        <Link
          component='button'
          underline='hover'
          sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
          onClick={() => onViewPixelClick(row)}
        >
          {value}
        </Link>
      ),
      flex: 3,
    },
    {
      field: 'eventId',
      headerName: 'ID',
      flex: 0.7,
    },
    {
      field: 'advertiser',
      headerName: 'Advertiser',
      align: 'center',
      valueFormatter: (value: IControlOption) => value?.label,
      display: 'flex',
      renderCell: ControlOptionChip,
      flex: 1.5,
    },
    {
      field: 'segment',
      headerName: 'Segment',
      valueFormatter: (value: IControlOption) => fallbackValueFormatter(value?.label),
      flex: 2,
    },
    {
      field: 'dateCreated',
      headerName: 'Date Created',
      valueFormatter: (value: string) => dateToShortFormat(value),
      flex: 1.5,
    },
    {
      field: 'firedToday',
      headerName: 'Fired Today',
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      renderCell: ({ value }: GridRenderCellParams) => (
        <LoadingCellWrapper loading={loadingMetrics}>{value ? numberToUsFormat(value) : '—'}</LoadingCellWrapper>
      ),
    },
    {
      field: 'firedLast7Days',
      headerName: 'Fired Last 7 Days',
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      renderCell: ({ value }: GridRenderCellParams) => (
        <LoadingCellWrapper loading={loadingMetrics}>{value ? numberToUsFormat(value) : '—'}</LoadingCellWrapper>
      ),
      flex: 1.4,
    },
    {
      field: 'status',
      headerName: 'Status',
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      renderCell: ({ value }: GridRenderCellParams) => (
        <LoadingCellWrapper loading={loadingMetrics}>
          <StatusChip
            {...getActiveInactiveStatusProps(value === PixelStatus.Firing, PixelStatus.Firing, PixelStatus.NotFiring)}
          />
        </LoadingCellWrapper>
      ),
      filterOperators: pixelsStatusOperators,
    },
    {
      field: 'pixelsFiredByDay',
      headerName: 'Pixels Fired By Day',
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      valueFormatter: (value: IParsedEvent['pixelsFiredByDay']) =>
        (value ?? [])
          .filter(record => record?.value)
          .map(record => `${record.date}: ${record.value}`)
          .join(', '),
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <LoadingCellWrapper loading={loadingMetrics}>
          <PixelFiredByDayCell {...params} />
        </LoadingCellWrapper>
      ),
      flex: 2,
    },
    {
      field: 'active',
      headerName: 'Active',
      align: 'center',
      sortable: false,
      headerAlign: 'center',
      minWidth: 150,
      flex: 2,
      disableExport: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <>
          <ViewButton {...params} onClick={onViewPixelClick} />
          {isAdmin && <ArchiveButton {...params} onClick={archivePixel} />}
        </>
      ),
    },
  ].map(item => ({
    ...item,
    flex: item.flex ?? 1,
    getApplyQuickFilterFn: item.getApplyQuickFilterFn || defaultQuickFilter,
  })) as GridColDef[];
